import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

export interface AuthContextType {
    user: User | null
    session: Session | null
    userProfile: any | null
    loading: boolean
    profileLoading: boolean
    profileNotFound: boolean
    signIn: (email: string, password: string) => Promise<any>
    signUp: (email: string, password: string, userData?: any) => Promise<any>
    signOut: () => Promise<void>
    resetPassword: (email: string) => Promise<any>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

interface AuthProviderProps {
    children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [userProfile, setUserProfile] = useState<any | null>(null)
    const [loading, setLoading] = useState(true)
    const [profileLoading, setProfileLoading] = useState(false)
    const [profileNotFound, setProfileNotFound] = useState(false)
    const fetchingProfile = useRef(false)
    const currentUserId = useRef<string | null>(null)

    const fetchProfileAndSetUser = async (sessionUser: User | null) => {
        currentUserId.current = sessionUser?.id ?? null
        setUser(sessionUser)
        if (sessionUser) {
            await fetchUserProfile(sessionUser)
        } else {
            setUserProfile(null)
            setLoading(false)
        }
    }

    useEffect(() => {
        let mounted = true

        // getSession() reads from localStorage without using the refresh token if access token is still valid
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!mounted) return
            setSession(session)
            fetchProfileAndSetUser(session?.user ?? null)
        })

        // Skip INITIAL_SESSION — already handled by getSession() above.
        // This prevents double refresh token usage in React Strict Mode (dev double-mount).
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'INITIAL_SESSION') return
            if (!mounted) return
            console.log("Auth state change:", event, session?.user?.email || 'No session')

            // Tab focus / token refresh fires SIGNED_IN again — skip if same user already loaded
            if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user?.id && session.user.id === currentUserId.current) {
                setSession(session)
                return
            }

            setSession(session)
            fetchProfileAndSetUser(session?.user ?? null)
        })

        return () => {
            mounted = false
            subscription.unsubscribe()
        }
    }, [])

    const fetchUserProfile = async (user: User) => {
        // Prevent multiple simultaneous calls
        if (fetchingProfile.current) {
            console.log("Profile fetch already in progress, skipping...")
            return
        }

        fetchingProfile.current = true
        setProfileLoading(true)
        setProfileNotFound(false)

        console.log("🔍 Fetching user profile for:", user.email)

        try {
            if (!user.email) {
                throw new Error('User email is required but not available')
            }

            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('email', user.email)
                .is("deleted_dt", null)
                .maybeSingle()

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching user profile:', error)
                setUserProfile(null)
                setProfileNotFound(true)
            } else {
                console.log("✅ User profile fetched:", data)
                setUserProfile(data)
                setProfileNotFound(data === null)
            }
        } catch (error) {
            console.error('Exception in fetchUserProfile:', error)
            setUserProfile(null)
            setProfileNotFound(true)
        } finally {
            fetchingProfile.current = false
            setProfileLoading(false)
            setLoading(false)
        }
    }

    const signIn = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        })
        return { data, error }
    }

    const signUp = async (email: string, password: string, userData?: any) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: userData,
            },
        })

        // Handle all authentication errors here
        if (error) {
            console.log("SignUp error:", error)

            // Handle specific error cases
            if (error.message && error.message.includes('not authorized to sign up')) {
                return {
                    data,
                    error: {
                        ...error,
                        message: 'Cet e-mail n\'est pas autorisé à s\'inscrire. Veuillez contacter un administrateur pour obtenir l\'accès.'
                    }
                }
            }

            // Return the original error for other cases
            return { data, error }
        }

        // Success case
        return { data, error: null }
    }

    const signOut = async () => {
        try {
            // Clear state immediately for better UX
            setUser(null)
            setSession(null)
            setUserProfile(null)
            setProfileLoading(false)
            setProfileNotFound(false)

            // Sign out from Supabase (this clears localStorage)
            const { error } = await supabase.auth.signOut()
            if (error) {
                console.error('Error signing out:', error)
                throw error
            }

            console.log('Successfully signed out')
        } catch (error) {
            console.error('Error during sign out:', error)
            throw error
        }
    }

    const resetPassword = async (email: string) => {
        // The recovery email carries a one-time code typed on /reset-password: a link
        // gets consumed by corporate mail scanners before the user clicks it.
        // redirectTo only serves emails still built from the former link template.
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        })
        return { data, error }
    }

    const value = {
        user,
        session,
        userProfile,
        loading,
        profileLoading,
        profileNotFound,
        signIn,
        signUp,
        signOut,
        resetPassword,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 